import { type Error, getApiErrorMessage, useAsyncData } from '#imports'
import type { Ref } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import type { ThresholdKind } from '~/api/shipping/thresholds.types'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import type { ShippingCategoryGroupMetadata } from '../../../CategoryList.types'
import { transformShippingValidationThresholds } from '../RuleDrawer.utils'

export function useHttpGetThresholds(
  shippingCategoryGroupId: Ref<ShippingCategoryGroupMetadata['id']>,
  markets: Ref<MarketCountryCode[]>,
  thresholdKinds: ThresholdKind[],
  triggerError = false,
) {
  const { openErrorToast } = useTheToast()
  const { publicId: sellerId } = useSellerInfoStore()
  const $httpFetch = useHttpFetch()

  const { data, error } = useAsyncData(
    () =>
      `shipping-thresholds-${shippingCategoryGroupId.value}-${markets.value.join(',')}-${thresholdKinds.join(',')}`,
    async () => {
      if (!markets.value.length || !sellerId) {
        return null
      }

      const response = await $httpFetch(
        '/bm/delivery-methods/v1/seller/:sellerId/validation-thresholds',
        {
          pathParams: { sellerId },
          query: {
            shippingCategoryGroupId: shippingCategoryGroupId.value,
            markets: markets.value,
            thresholdKinds,
          },
        },
      )

      return transformShippingValidationThresholds(response)
    },
    {
      watch: [shippingCategoryGroupId, markets],
    },
  )

  if (triggerError && error.value) {
    openErrorToast({
      content: getApiErrorMessage(error.value as unknown as Error),
    })
  }

  return { data, error }
}
